/**
 * The small shared pieces. Deliberately plain: this is a tool three groups of
 * people use on a phone between other jobs, not something anyone browses.
 */
import { ATTACHMENT_CONTENT_TYPES } from '@bjsfabric/shared'
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react'

import { api, ApiError } from './api/client'
import { useTheme } from './theme'

/**
 * Refetches when the tab regains focus — a mill or buyer who left a screen
 * open across someone else's action (a reply, a quote, a confirmation) would
 * otherwise keep looking at the moment they opened it until they happen to
 * navigate away and back. There is no websocket here and this app does not
 * poll; catching "came back to it" is the cheap version of watching for that.
 *
 * `load` is read from a ref rather than taken as a dependency, so a screen
 * whose `load` closure is recreated every render (most of them, since none
 * memoise it) does not tear down and re-attach these listeners on every
 * keystroke — they go up once and always call whatever `load` most recently
 * was.
 */
export function useRefreshOnFocus(load: () => void) {
  const latest = useRef(load)
  latest.current = load

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') latest.current()
    }
    window.addEventListener('focus', onVisible)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onVisible)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string
  children: ReactNode
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && !error ? <span className="field-hint">{hint}</span> : null}
      {error ? <span className="field-error">{error}</span> : null}
    </label>
  )
}

export function Button({
  children,
  busy,
  variant = 'primary',
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  busy?: boolean
  variant?: 'primary' | 'quiet' | 'danger'
}) {
  return (
    <button className={`button button-${variant}`} disabled={busy || rest.disabled} {...rest}>
      {busy ? '…' : children}
    </button>
  )
}

/** One tap between the dark palette this is designed for and a light one for
 *  a bright shop floor. `floating` pins it top-right for the screens that
 *  have no topbar of their own to sit inside. */
export function ThemeToggle({ floating = false }: { floating?: boolean }) {
  const [theme, setTheme] = useTheme()
  const next = theme === 'dark' ? 'light' : 'dark'

  const button = (
    <button
      type="button"
      className="button button-quiet theme-toggle"
      onClick={() => setTheme(next)}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
    >
      {theme === 'dark' ? '☀️' : '🌙'}
    </button>
  )

  return floating ? <div className="theme-toggle-floating">{button}</div> : button
}

/**
 * However an invite got made — a new person, or a whole new organisation —
 * this is how it leaves the app. There is no mail transport (see the
 * README), so the admin has always passed these links on by hand; this just
 * saves him the copy-paste. `phone`, when the organisation gave one at
 * sign-up, opens the chat with them directly instead of a bare contact
 * picker.
 */
export function ShareInvite({
  url,
  message,
  phone,
}: {
  url: string
  message: string
  phone?: string | null
}) {
  const text = `${message} ${url}`

  const share = async () => {
    // The Web Share API hands the OS's own share sheet to the browser —
    // WhatsApp included, alongside whatever else is installed — which beats
    // guessing at one app when there's a choice on the phone in question.
    if (navigator.share) {
      try {
        await navigator.share({ text })
        return
      } catch {
        // Cancelled, or unsupported despite existing — either way, WhatsApp's
        // own link below still works.
      }
    }
    const digits = phone?.replace(/[^\d]/g, '')
    const target = digits ? `https://wa.me/${digits}` : 'https://wa.me/'
    window.open(`${target}?text=${encodeURIComponent(text)}`, '_blank', 'noopener')
  }

  return (
    <>
      <div className="invite">{url}</div>
      <div className="actions">
        <Button type="button" variant="quiet" onClick={() => void share()}>
          Share on WhatsApp
        </Button>
      </div>
    </>
  )
}

/** One place that turns a thrown error into something a person can read. */
export function Problem({ error }: { error: unknown }) {
  if (!error) return null

  if (error instanceof ApiError) {
    return (
      <div className="problem" role="alert">
        <p>{error.message}</p>
        {error.issues.length > 0 ? (
          <ul>
            {error.issues.map((issue) => (
              <li key={issue.path}>
                {issue.path}: {issue.message}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    )
  }

  return (
    <div className="problem" role="alert">
      <p>{error instanceof Error ? error.message : 'Something went wrong'}</p>
    </div>
  )
}

/** `label` overrides the text shown without changing the colour, which still keys off `value`. */
export function Pill({ value, label }: { value: string; label?: string }) {
  return <span className={`pill pill-${value.toLowerCase()}`}>{label ?? value.replace(/_/g, ' ')}</span>
}

/**
 * A category, not a status — `BUYER` vs `COMPANY` on an organisation, say.
 * Deliberately its own class rather than `Pill`: nothing here is good, bad, or
 * pending, so it shouldn't compete with the traffic-light colours a status
 * pill uses.
 */
export function Tag({ children }: { children: ReactNode }) {
  return <span className="tag">{children}</span>
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>
}

export function Loading() {
  return <p className="empty">Loading…</p>
}

/**
 * Measurements arrive as strings and are shown as strings.
 *
 * They are `numeric` in the database so they are never rounded, and turning
 * them into JavaScript numbers here just to print them would reintroduce that
 * risk at the last possible step.
 */
export function measurement(value: string | number | null, unit?: string | null): string {
  if (value === null || value === undefined || value === '') return '—'
  return unit ? `${value} ${unit}` : String(value)
}

/** A bare number of days is ambiguous next to a price and a quantity. */
export function leadTime(days: number | null): string {
  if (days === null || days === undefined) return '—'
  return days === 1 ? '1 day' : `${days} days`
}

export function shortDate(value: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * How much of the promised lead time has passed since the buyer confirmed.
 *
 * Purely a display, computed from `confirmedAt` and the lead time already
 * written by the quote/confirm flow — no new column, no new status. Renders
 * nothing until both are known, which is before `IN_PROGRESS` and for anyone
 * who was never told either value in the first place.
 */
export function DeliveryProgress({
  confirmedAt,
  leadTimeDays,
}: {
  confirmedAt: string | null | undefined
  leadTimeDays: number | null | undefined
}) {
  if (!confirmedAt || leadTimeDays == null || leadTimeDays <= 0) return null

  const dayMs = 24 * 60 * 60 * 1000
  const start = new Date(confirmedAt).getTime()
  if (Number.isNaN(start)) return null

  const dueAt = start + leadTimeDays * dayMs
  const fraction = Math.max(0, Math.min(1, (Date.now() - start) / (dueAt - start)))
  const daysLeft = Math.ceil((dueAt - Date.now()) / dayMs)
  const overdue = daysLeft < 0

  return (
    <div style={{ marginTop: 10 }}>
      <div className="progress-track">
        <div
          className={`progress-fill${overdue ? ' progress-fill-overdue' : ''}`}
          style={{ width: `${Math.round(fraction * 100)}%` }}
        />
      </div>
      <p className="field-hint" style={{ marginTop: 4, marginBottom: 0 }}>
        {overdue
          ? `Overdue by ${Math.abs(daysLeft)} ${Math.abs(daysLeft) === 1 ? 'day' : 'days'}`
          : daysLeft === 0
            ? 'Due today'
            : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`}
      </p>
    </div>
  )
}

export interface AttachmentView {
  id: string
  filename: string
  contentType: string
  sizeBytes: number
  uploadedByRole: 'ADMIN' | 'BUYER' | 'COMPANY'
  createdAt: string
}

const UPLOADER_LABEL: Record<AttachmentView['uploadedByRole'], string> = {
  ADMIN: 'Admin',
  BUYER: 'Buyer',
  COMPANY: 'Mill',
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * The photographed sheet, mostly -- an optional image or PDF on a requirement
 * or an enquiry. One primitive for both: `kind`/`id` pick which endpoint pair
 * to call (`api/src/attachments/routes.ts`), `canUpload` is off for a
 * read-only viewer (the admin looking at what a mill attached).
 *
 * Opening a file goes through `api.download` + `URL.createObjectURL`, not a
 * plain `<a href>` -- see the comment on `api.download` for why a bare link
 * would 401.
 */
export function AttachmentsPanel({
  kind,
  id,
  canUpload = false,
  title = 'Attachments',
}: {
  kind: 'requirement' | 'enquiry'
  id: string
  canUpload?: boolean
  /** Distinguishes two panels on one screen -- the mill's enquiry page shows
   *  both the requirement's (read-only) and this enquiry's own. */
  title?: string
}) {
  const basePath = kind === 'requirement' ? `/requirements/${id}/attachments` : `/enquiries/${id}/attachments`
  const [items, setItems] = useState<AttachmentView[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    try {
      const result = await api.get<{ attachments: AttachmentView[] }>(basePath)
      setItems(result.attachments)
    } catch (problem) {
      setError(problem)
    }
  }, [basePath])

  useEffect(() => {
    void load()
  }, [load])

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.upload<{ attachment: AttachmentView }>(basePath, file)
      setItems((current) => [...(current ?? []), result.attachment])
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  const open = async (attachment: AttachmentView) => {
    setError(null)
    try {
      const blob = await api.download(`/attachments/${attachment.id}/file`)
      window.open(URL.createObjectURL(blob), '_blank', 'noopener')
    } catch (problem) {
      setError(problem)
    }
  }

  if (items === null) return null

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <Problem error={error} />
      {items.length === 0 ? (
        <Empty>No files attached yet.</Empty>
      ) : (
        <ul className="attachment-list">
          {items.map((item) => (
            <li key={item.id}>
              <button type="button" className="attachment-link" onClick={() => void open(item)}>
                {item.filename}
              </button>
              <span className="field-hint">
                {formatBytes(item.sizeBytes)} · {UPLOADER_LABEL[item.uploadedByRole]} attached ·{' '}
                {shortDate(item.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {canUpload ? (
        <div className="actions" style={{ marginTop: 10 }}>
          <input
            ref={inputRef}
            type="file"
            accept={ATTACHMENT_CONTENT_TYPES.join(',')}
            onChange={(event) => void onFile(event)}
            style={{ display: 'none' }}
          />
          <Button
            type="button"
            variant="quiet"
            busy={busy}
            onClick={() => inputRef.current?.click()}
          >
            Attach a file
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export interface CatalogFabricView {
  fabricCode: string
  structure: string | null
  fibre: string | null
  yarnCountRaw: string | null
  dyeMethod: string | null
  bedType: string | null
  stitchLengthMm: string | null
  construction: string | null
}

function titleish(value: string): string {
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

/**
 * Read-only. The data already rides in on the requirement/enquiry payload
 * (`RequirementsService.present` / `EnquiriesService`'s `presentForCompany`),
 * so unlike `AttachmentsPanel` this never fetches anything of its own.
 */
export function CatalogFabricPanel({ fabric }: { fabric: CatalogFabricView }) {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>From the catalogue</h3>
      <ul className="specs">
        <li>
          Code <b>{fabric.fabricCode}</b>
        </li>
        {fabric.structure ? (
          <li>
            Structure <b>{titleish(fabric.structure)}</b>
          </li>
        ) : null}
        {fabric.fibre ? (
          <li>
            Fibre <b>{titleish(fabric.fibre)}</b>
          </li>
        ) : null}
        {fabric.yarnCountRaw ? (
          <li>
            Yarn <b>{fabric.yarnCountRaw}</b>
          </li>
        ) : null}
        {fabric.dyeMethod ? (
          <li>
            Dye <b>{titleish(fabric.dyeMethod)}</b>
          </li>
        ) : null}
        {fabric.construction ? (
          <li>
            Construction <b>{titleish(fabric.construction)}</b>
          </li>
        ) : null}
        {fabric.bedType ? (
          <li>
            Bed <b>{titleish(fabric.bedType)}</b>
          </li>
        ) : null}
        {fabric.stitchLengthMm ? (
          <li>
            Stitch length <b>{fabric.stitchLengthMm} mm</b>
          </li>
        ) : null}
      </ul>
    </div>
  )
}
